<template>
  <div>
    <transition name="fade">
      <div
        class="cookie-banner"
        v-if="!acceptedCookies && !cookieDialog.isOpen"
      >
        <p>
          {{
            $t("app.components.global.cookiebanner.this_website_uses_cookies")
          }}
        </p>
        <div class="button-group">
          <button
            class="cta-button cta-button--secondary"
            @click="openCookieDialog"
          >
            {{ $t("app.components.global.cookiebanner.configure_cookies") }}
          </button>
          <button
            class="cta-button cta-button--primary"
            @click="acceptCookies(true, false)"
          >
            {{ $t("app.components.global.cookiebanner.accept_all") }}
          </button>
        </div>
      </div>
    </transition>

    <div
      class="cookie-change-icon"
      v-if="acceptedCookies"
      @click="openCookieDialog"
    >
      <FontAwesome :icon="['fas', 'cookie']" class="icon" />
    </div>

    <transition name="fade">
      <div
        v-if="cookieDialog.isOpen"
        class="cookie-dialog-outer"
        @click.self="closeCookieDialog"
      >
        <div class="cookie-dialog-inner">
          <h2>Cookie Preferences</h2>
          <div class="cookie-options">
            <div class="cookie-option">
              <input
                type="checkbox"
                id="technical"
                v-model="cookieDialog.technical"
                disabled
              />
              <label for="technical">
                {{ $t("app.components.global.cookiebanner.technical_cookies") }}
              </label>
            </div>
            <p>
              {{
                $t(
                  "app.components.global.cookiebanner.technical_cookies_description"
                )
              }}
            </p>

            <div class="cookie-option">
              <input
                type="checkbox"
                id="marketing"
                v-model="cookieDialog.marketing"
              />
              <label for="marketing">
                {{ $t("app.components.global.cookiebanner.marketing_cookies") }}
              </label>
            </div>
            <p>
              {{
                $t(
                  "app.components.global.cookiebanner.marketing_cookies_description"
                )
              }}
            </p>

            <div class="cookie-option">
              <input
                type="checkbox"
                id="analytics"
                v-model="cookieDialog.analytics"
              />
              <label for="analytics">
                {{ $t("app.components.global.cookiebanner.analytics_cookies") }}
              </label>
            </div>
            <p>
              {{
                $t(
                  "app.components.global.cookiebanner.analytics_cookies_description"
                )
              }}
            </p>
          </div>

          <a href="/legal/privacy" target="_blank">
            {{ $t("app.components.global.cookiebanner.privacy_policy") }}
          </a>

          <div class="button-group">
            <button
              class="cta-button cta-button--secondary"
              @click="acceptCookies(false, true)"
            >
              {{ $t("app.components.global.cookiebanner.decline_all") }}
            </button>
            <button
              class="cta-button cta-button--secondary"
              @click="acceptCookies(false, false)"
            >
              {{ $t("app.components.global.cookiebanner.accept_selected") }}
            </button>
            <button
              class="cta-button cta-button--primary"
              @click="acceptCookies(true, false)"
            >
              {{ $t("app.components.global.cookiebanner.accept_all") }}
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>
<script setup lang="ts">
// Cookie dialog
const cookieDialog = ref({
  isOpen: false,
  technical: true,
  marketing: false,
  analytics: false,
});

const openCookieDialog = () => {
  cookieDialog.value.isOpen = true;
};

const closeCookieDialog = () => {
  cookieDialog.value.isOpen = false;
};

// Cookie banner
type StoredCookies = {
  dialogClicked: boolean;
  lastDialogDate: Date;
  acceptedCookies: {
    technical: boolean;
    marketing: boolean;
    analytics: boolean;
  };
};
const acceptedCookies = ref(null) as Ref<null | StoredCookies>;

const acceptCookies = (acceptAll = false, declineAll = false) => {
  if (acceptAll) {
    cookieDialog.value.technical = true;
    cookieDialog.value.marketing = true;
    cookieDialog.value.analytics = true;
  }

  if (declineAll) {
    cookieDialog.value.technical = true; // Always accept technical cookies
    cookieDialog.value.marketing = false;
    cookieDialog.value.analytics = false;
  }

  acceptedCookies.value = {
    dialogClicked: true,
    lastDialogDate: new Date(),
    acceptedCookies: {
      technical: cookieDialog.value.technical,
      marketing: cookieDialog.value.marketing,
      analytics: cookieDialog.value.analytics,
    },
  };

  try {
    const cookieSettings = useCookie("cookieSettings");
    cookieSettings.value = JSON.stringify(acceptedCookies.value);

    // Set consents
    setConsents();
  } catch (error) {
    console.error("Error setting accepted cookies", error);
  }

  cookieDialog.value.isOpen = false;
};

const loadAcceptedCookies = () => {
  try {
    const cookieSettings = useCookie("cookieSettings");
    const loadedAcceptedCookies =
      cookieSettings.value as unknown as StoredCookies;
    if (loadedAcceptedCookies) {
      console.log(loadedAcceptedCookies);

      try {
        if (
          loadedAcceptedCookies.dialogClicked &&
          new Date(loadedAcceptedCookies.lastDialogDate) >
            new Date(new Date().setDate(new Date().getDate() - 30))
        ) {
          acceptedCookies.value = loadedAcceptedCookies;

          // Pre-fill cookie dialog with current settings
          cookieDialog.value.technical =
            loadedAcceptedCookies.acceptedCookies.technical;
          cookieDialog.value.marketing =
            loadedAcceptedCookies.acceptedCookies.marketing;
          cookieDialog.value.analytics =
            loadedAcceptedCookies.acceptedCookies.analytics;
        }
      } catch (error) {
        console.error("Error parsing accepted cookies", error);
        acceptedCookies.value = null;
      }
    }
  } catch (error) {
    console.error("Error getting accepted cookies", error);
  }
};

const setConsents = () => {
  if (acceptedCookies.value) {
    // Set cookie consents
    if (acceptedCookies.value.acceptedCookies.technical) {
      // Set technical cookies
    }

    if (acceptedCookies.value.acceptedCookies.marketing) {
      // Set marketing cookies
    }

    if (acceptedCookies.value.acceptedCookies.analytics) {
      // Set analytics cookies
      const { gtag, enableAnalytics } = useGtag();
      gtag("consent", "update", {
        ad_user_data: "granted",
        ad_personalization: "granted",
        ad_storage: "granted",
        analytics_storage: "granted",
      });

      enableAnalytics();
    } else {
      // Disable analytics cookies
      const { gtag, disableAnalytics } = useGtag();
      gtag("consent", "update", {
        ad_user_data: "denied",
        ad_personalization: "denied",
        ad_storage: "denied",
        analytics_storage: "denied",
      });

      disableAnalytics();
    }
  }
};

onBeforeMount(() => {
  loadAcceptedCookies();

  // Set consents on initial load
  setConsents();
});
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.cookie-banner {
  background: #fff;
  border-radius: 8px;
  padding: 16px;
  position: fixed;
  bottom: 20px;
  left: 20px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  text-align: center;
  max-width: 600px;
  z-index: 1000;
}

.button-group {
  display: flex;
  gap: 8px;
  justify-content: center;
  margin-top: 12px;
}

.cookie-change-icon {
  position: fixed;
  bottom: 20px;
  left: 20px;
  width: 40px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: #fff;
  border-radius: 50%;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  cursor: pointer;
}

.cookie-dialog-outer {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
}

.cookie-dialog-inner {
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  margin-top: 10dvh;
  width: 60%;
  max-width: 600px;
  text-align: center;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);

  overflow-y: scroll;
  max-height: 80dvh;
}

.cookie-options {
  margin: 20px 0;
}

.cookie-option {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.cookie-dialog-inner p {
  text-align: left;
  margin-bottom: 20px;
}

/* Button styles */
.cta-button {
  padding: 0.5rem 1.4rem;
  border-radius: 6px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: row;
}

.cta-button--primary {
  background: var(--t-accent);
  color: white;
  border: none;
}

.cta-button--primary:hover {
  background: var(--t-accent);
}

.cta-button--secondary {
  background: transparent;
  border: 2px solid var(--t-accent);
  color: var(--t-accent);
}

.cta-button--secondary:hover {
  background: var(--t-accent);
  color: white;
}

@media (max-width: 768px) {
  .cookie-dialog-inner {
    width: 80%;
  }

  .button-group {
    flex-direction: column;
  }
}
</style>
